import { styled } from '@linaria/react';
import dayjs from 'dayjs';
import type { RoadmapInitiative } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { safeParseDateInput } from '../../../../helpers/date-time-helpers/parsing/parsing';
import { primaryBase, primaryLightest, purpleBase, redBase } from '../../../../styles/design-tokens';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import {
  calculateActivityExtensions,
  calculateForecastLinePosition,
  calculateInitiativeBarPosition,
  matchesDeliveryProgressFilter,
  matchesYearFilter,
} from '../roadmap.helpers';
import type { DeliveryProgressFilter } from '../roadmap.type';
import { LEFT_PADDING } from '../roadmap.type';
import { InitiativeTooltipContent } from './initiative-tooltip-content';
interface InitiativeBarProps {
  initiative: RoadmapInitiative;
  timelineStart: dayjs.Dayjs;
  timelineWidth: string | number;
  totalDays: number;
  deliveryProgressFilter: DeliveryProgressFilter;
  selectedYear: number | null;
}

export function InitiativeBar({
  initiative,
  timelineStart,
  timelineWidth,
  totalDays,
  deliveryProgressFilter,
  selectedYear,
}: InitiativeBarProps) {
  const i18n = useTranslation();
  // Apply year filter
  if (!matchesYearFilter(initiative.start_date, initiative.end_date, selectedYear)) {
    return null;
  }

  // Apply delivery progress filter
  const progress = typeof initiative.progress === 'number' ? initiative.progress : 0;
  if (!matchesDeliveryProgressFilter(progress, deliveryProgressFilter)) {
    return null;
  }

  const barPosition = calculateInitiativeBarPosition(initiative, timelineStart, totalDays);
  if (!barPosition) return null;

  let { left: leftPercent, width: widthPercent } = barPosition;

  // Clip bar to timeline boundaries (in percentages)
  // If bar starts before timeline, move it and reduce width
  if (leftPercent < 0) {
    widthPercent += leftPercent; // Reduce width by the amount it extends past the start
    leftPercent = 0;
  }

  // If bar extends past the end of timeline, reduce width
  if (leftPercent + widthPercent > 100) {
    widthPercent = 100 - leftPercent;
  }

  // Don't render if bar has no visible width
  if (widthPercent <= 0) return null;

  // Calculate activity extensions
  const extensions = calculateActivityExtensions(initiative, timelineStart, totalDays);

  // Calculate forecast line position
  const forecastLine = calculateForecastLinePosition(initiative, timelineStart, totalDays);

  return (
    <InitiativeRow>
      <InitiativeLabel>
        {initiative.key && <InitiativeKey>{initiative.key}</InitiativeKey>}
        <InitiativeName>{initiative.name}</InitiativeName>
      </InitiativeLabel>
      <TimelineArea style={{ width: timelineWidth }}>
        {/* Left activity date line with dot */}
        {extensions?.leftExtension && (
          <Tooltip
            label={
              <InitiativeTooltipContent
                initiativeKey={initiative.key}
                initiativeName={initiative.name}
                progress={progress}
                metadataRows={[
                  { label: 'Target Start Date:', value: formatDate(initiative.start_date) },
                  { label: 'Activity Start Date:', value: formatDate(initiative.activity_start_date) },
                  {
                    value: `-${safeParseDateInput(initiative.start_date)?.startOf('day').diff(safeParseDateInput(initiative.activity_start_date)?.startOf('day'), 'days')} days`,
                  },
                ]}
              />
            }
            variant="black"
          >
            <ActivityDateLineContainer
              style={{
                left: `${Math.max(0, extensions.leftExtension.left)}%`,
                width: `${Math.min(extensions.leftExtension.width, extensions.leftExtension.left + extensions.leftExtension.width)}%`,
              }}
            >
              <ActivityDateLine />
              <ActivityDateDot style={{ left: 0 }} />
            </ActivityDateLineContainer>
          </Tooltip>
        )}

        {/* Main initiative bar */}
        <Tooltip
          label={
            <InitiativeTooltipContent
              initiativeKey={initiative.key}
              initiativeName={initiative.name}
              progress={progress}
              metadataRows={[
                {
                  value: `${formatDate(initiative.start_date)} - ${formatDate(initiative.end_date)}`,
                },
              ]}
            />
          }
          variant="black"
          multiline
        >
          <InitiativeBarStyled style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}>
            <BarHeader>
              <BarTypeLabel>{i18n.t('common.initiative', 'Initiative').toUpperCase()}</BarTypeLabel>
            </BarHeader>
            <BarDataSection>
              <BarContent>
                {initiative.key && <BarKeyLabel>#{initiative.key}</BarKeyLabel>}
                <BarLabel>{initiative.name}</BarLabel>
              </BarContent>
              <CompletionPercentage>{Math.round(progress)}%</CompletionPercentage>
            </BarDataSection>
          </InitiativeBarStyled>
        </Tooltip>

        {/* Right activity date line with dot */}
        {extensions?.rightExtension && (
          <Tooltip
            label={
              <InitiativeTooltipContent
                initiativeKey={initiative.key}
                initiativeName={initiative.name}
                progress={progress}
                metadataRows={[
                  { label: 'Target End Date:', value: formatDate(initiative.end_date) },
                  { label: 'Activity End Date:', value: formatDate(initiative.activity_end_date) },
                  {
                    value: `+${safeParseDateInput(initiative.activity_end_date)?.startOf('day').diff(safeParseDateInput(initiative.end_date)?.startOf('day'), 'days')} days`,
                    color: redBase,
                  },
                ]}
              />
            }
            variant="black"
          >
            <ActivityDateLineContainer
              style={{
                left: `${extensions.rightExtension.left}%`,
                width: `${Math.min(extensions.rightExtension.width, 100 - extensions.rightExtension.left)}%`,
              }}
            >
              <ActivityDateLine />
              <ActivityDateDot style={{ right: 0 }} />
            </ActivityDateLineContainer>
          </Tooltip>
        )}

        {/* Forecast date line with dot */}
        {forecastLine && (
          <Tooltip
            label={
              <InitiativeTooltipContent
                initiativeKey={initiative.key}
                initiativeName={initiative.name}
                progress={progress}
                metadataRows={[
                  { label: 'Activity End Date:', value: formatDate(initiative.activity_end_date) },
                  {
                    label: 'Forecasted End Date',
                    value: formatDate(initiative.forecasts?.forecasted_end_dates?.recommended?.['95_percent']),
                  },
                  {
                    value: `+${safeParseDateInput(initiative.forecasts?.forecasted_end_dates?.recommended?.['95_percent'])?.startOf('day').diff(safeParseDateInput(initiative.activity_end_date)?.startOf('day'), 'days')} days`,
                    color: purpleBase,
                  },
                ]}
              />
            }
            variant="black"
          >
            <ActivityDateLineContainer
              style={{
                left: `${forecastLine.left}%`,
                width: `${Math.min(forecastLine.width, 100 - forecastLine.left)}%`,
              }}
            >
              <ForecastDateLine />
              <ActivityDateDot style={{ right: 0, backgroundColor: purpleBase }} />
            </ActivityDateLineContainer>
          </Tooltip>
        )}
      </TimelineArea>
    </InitiativeRow>
  );
}

// Styled components
const InitiativeRow = styled.div`
  height: 70px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--sky-lightest);
  width: 100%;
  transition: background 0.15s ease;
  position: relative;

  &:hover {
    background: var(--sky-lightest);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const InitiativeLabel = styled.div`
  width: ${LEFT_PADDING}px;
  padding: 0 16px 0 48px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  overflow: hidden;
`;

const InitiativeKey = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #d84315;
  flex-shrink: 0;
  background: #ffe0b2;
  padding: 3px 7px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid #ffccbc;
`;

const InitiativeName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TimelineArea = styled.div`
  position: relative;
  height: 100%;
  border-left: 1px solid var(--sky-light);
  border-right: 1px solid var(--sky-light);
  flex: 1;
  overflow: visible;
`;

const InitiativeBarStyled = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 50px;
  background: #ffffff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 152, 0, 0.2);

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    transform: translateY(-50%) translateY(-1px);
  }
`;

const BarHeader = styled.div`
  background: #ff9800;
  padding: 3px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 16px;
`;

const BarDataSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 10px;
  flex: 1;
  background: #ffffff;
  gap: 8px;
`;

const BarContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
`;

const BarKeyLabel = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: #d84315;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
`;

const BarTypeLabel = styled.div`
  font-size: 8px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
`;

const BarLabel = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #333333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
`;

const CompletionPercentage = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${primaryBase};
  background: ${primaryLightest};
  text-align: right;
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: 4px;
`;

const ActivityDateLineContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 2px;
  display: flex;
  align-items: center;
`;

const ActivityDateLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #ff9800;
  pointer-events: none;
`;

const ActivityDateDot = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: #ff9800;
  border-radius: 50%;
  pointer-events: none;
  z-index: 10;
`;

const ForecastDateLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  border-top: 2px dashed ${purpleBase};
  background: none;
  pointer-events: none;
`;
